import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  Usuario,
  Lead,
  LostLeadCard,
  LostLeadReason,
  Cliente,
  Mensagem,
  WhatsAppAccount,
  Anexo,
  LoginRequest,
  LoginResponse,
  ListResponse,
  Organization,
  Plan,
  TrialInfo,
  RegisterRequest,
  RegisterResponse,
  OrganizationUsersResponse,
  OrganizationUser,
  InviteUserRequest,
  InviteUserResponse,
  UpdateOrganizationRequest,
  ChangePlanRequest,
  OrganizationUsageStats,
  DashboardGoals,
  DashboardStatsResponse,
  ListUnsubscribedPhonesResponse,
  BulkDeleteUnsubscribedResponse,
  PlansResponse,
  Subscription,
  BillingCycle,
  CheckoutSessionResponse,
  CustomerPortalResponse,
  SubscriptionResponse,
  // AI Types
  AISettings,
  PosVendasSettings,
  KanbanColumnLabels,
  AIRole,
  AIRoleTemplate,
  AISuggestion,
  Guideline,
  GuidelineTipo,
  CreateGuidelineRequest,
  CreateAIRoleRequest,
  PriceSource,
  CreatePriceSourceRequest,
  RagWebPage,
  CreateRagWebPageRequest,
  UpdateRagWebPageRequest,
  OrchestratorAgentPrompt,
  OrchestratorPromptPack,
  PromptCatalogItem,
  OrchestratorPromptCatalogResponse,
  SpecialistPrompt,
  SpecialistPromptScope,
  SpecialistReturnContract,
  Campaign,
  CampaignItem,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  CampaignItemPipelineStage,
  ImportedLead,
  Disparo,
  CreateDisparoRequest,
  DisparoStatus,
  PatchDisparoConfigRequest,
  AppNotification,
  AddCampaignItemsApiResult,
  AddCsvCampaignItemsApiResult,
  CsvTransientLeadInput,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3010/api';
// AI Service URL.
// - Em produção: REACT_APP_AI_SERVICE_URL ausente; cai em API_URL sem /api
//   (API Gateway roteia /ai/* pro ai-service Lambda).
// - Em local dev: backend e ai-service rodam em portas distintas (3010 vs 8080).
//   Defina REACT_APP_AI_SERVICE_URL=http://localhost:8080 no .env.local.
const AI_SERVICE_URL =
  process.env.REACT_APP_AI_SERVICE_URL || API_URL.replace(/\/api$/, '');

function toLostLeadCardFallback(lead: Lead): LostLeadCard {
  return {
    lostCardId: lead.leadId,
    organizationId: '',
    dedupeKey: `fallback:${lead.leadId}`,
    sourceLeadId: lead.leadId,
    nome: lead.nome,
    cpf: lead.cpf,
    telefone: lead.telefone,
    whatsappNumeroEntrada: lead.whatsappNumeroEntrada,
    observacao: lead.observacao,
    interessado: Boolean((lead.interessadoLigarEmMeses || 0) > 0),
    interessadoLigarEmMeses: lead.interessadoLigarEmMeses || null,
    interessadoLigarEmData: lead.interessadoLigarEmData || null,
    dataPerda: lead.updatedAt,
    pipelineStage: 'perdido',
    lostCategory: 'perdido',
    selecionado_backlog_perdidos: false,
    createdAt: lead.createdAt,
    updatedAt: lead.updatedAt,
  };
}

class ApiService {
  private api: AxiosInstance;
  private aiApi: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // AI Service client (routes to /ai/* directly)
    this.aiApi = axios.create({
      baseURL: AI_SERVICE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para adicionar token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Same interceptor for AI service
    this.aiApi.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Interceptor para tratar erros
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('usuario');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  }

  async getMe(): Promise<Usuario> {
    const response = await this.api.get<Usuario>('/auth/me');
    return response.data;
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await this.api.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, senha: string): Promise<{ message: string }> {
    const response = await this.api.post<{ message: string }>('/auth/reset-password', { token, senha });
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  // Leads
  async getLeads(filters?: {
    status?: Lead['status'];
    statusFechamento?: Lead['statusFechamento'];
    atendenteId?: string;
    lostPeriodMonths?: number;
    interestFilter?: 'all' | 'interested' | 'not_interested';
    onlyInterestedThisMonth?: boolean;
    sortBy?: 'updatedAt' | 'interessadoLigarEmData';
    sortOrder?: 'asc' | 'desc';
    inboundCampaignId?: string;
  }): Promise<Lead[]> {
    const params = filters
      ? Object.fromEntries(
          Object.entries(filters).filter(([, value]) => value !== undefined && value !== null)
        )
      : undefined;
    const response = await this.api.get<ListResponse<Lead>>('/leads', { params });
    return (response.data.leads as Lead[]) || [];
  }

  async getLead(leadId: string): Promise<Lead> {
    const response = await this.api.get<Lead>(`/leads/${leadId}`);
    return response.data;
  }

  async createLead(lead: Partial<Lead>): Promise<Lead> {
    const response = await this.api.post<Lead>('/leads', lead);
    return response.data;
  }

  async updateLead(leadId: string, lead: Partial<Lead>): Promise<Lead> {
    const response = await this.api.put<Lead>(`/leads/${leadId}`, lead);
    return response.data;
  }

  async updateLeadStatus(
    leadId: string,
    status: Lead['status'],
    statusFechamento?: Lead['statusFechamento'],
    posVendaStatus?: Lead['posVendaStatus']
  ): Promise<Lead> {
    const body: {
      status: Lead['status'];
      statusFechamento?: Lead['statusFechamento'];
      posVendaStatus?: Lead['posVendaStatus'];
    } = { status };
    if (statusFechamento) {
      body.statusFechamento = statusFechamento;
    }
    if (posVendaStatus) {
      body.posVendaStatus = posVendaStatus;
    }
    const response = await this.api.put<Lead>(`/leads/${leadId}/status`, body);
    return response.data;
  }

  async updateLeadAtendente(leadId: string, atendenteId: string): Promise<Lead> {
    const response = await this.api.put<Lead>(`/leads/${leadId}/atendente`, { atendenteId });
    return response.data;
  }

  async deleteLead(leadId: string): Promise<Lead> {
    // Exclusão lógica - muda status para 'excluido'
    const response = await this.api.put<Lead>(`/leads/${leadId}/status`, { status: 'excluido' });
    return response.data;
  }

  /** Descadastro manual (pedido do cliente) + arquivar lead como excluído */
  async registerLeadOptOut(leadId: string, body?: { reason?: string }): Promise<Lead> {
    const response = await this.api.post<Lead>(`/leads/${leadId}/opt-out`, body ?? {});
    return response.data;
  }

  // Lost Lead Cards
  async getLostLeadCards(filters?: {
    stage?: 'perdido' | 'em_campanha';
    lostCategory?: 'perdido' | 'inativo';
    lostPeriodMonths?: number;
    interestFilter?: 'all' | 'interested' | 'not_interested';
    onlyInterestedThisMonth?: boolean;
    sortBy?: 'updatedAt' | 'interessadoLigarEmData' | 'dataPerda';
    sortOrder?: 'asc' | 'desc';
    lostReason?: LostLeadReason;
    lostReasons?: LostLeadReason[];
  }): Promise<LostLeadCard[]> {
    let params: Record<string, string | number | boolean> | undefined;
    if (filters) {
      const { lostReasons, ...rest } = filters;
      const base = Object.fromEntries(
        Object.entries(rest).filter(([, value]) => value !== undefined && value !== null)
      ) as Record<string, string | number | boolean>;
      params =
        lostReasons && lostReasons.length > 0 ? { ...base, lostReasons: lostReasons.join(',') } : base;
      if (params && Object.keys(params).length === 0) params = undefined;
    }
    try {
      const response = await this.api.get<{ lostCards: LostLeadCard[]; total: number }>('/lost-cards', { params });
      return response.data.lostCards || [];
    } catch (error: any) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status !== 404) {
        throw error;
      }

      // Backward compatibility while backend route is not deployed.
      if (filters?.lostCategory === 'inativo') {
        return [];
      }
      const sortByFallback =
        filters?.sortBy === 'dataPerda' ? undefined : filters?.sortBy;
      const fallbackLeads = await this.getLeads({
        status: 'fechamento',
        statusFechamento: 'nao_vendido',
        lostPeriodMonths: filters?.lostPeriodMonths,
        interestFilter: filters?.interestFilter,
        onlyInterestedThisMonth: filters?.onlyInterestedThisMonth,
        sortBy: sortByFallback,
        sortOrder: filters?.sortOrder,
      });

      return fallbackLeads.map(toLostLeadCardFallback);
    }
  }

  async updateLostLeadCard(
    lostCardId: string,
    updates: Partial<Pick<LostLeadCard, 'pipelineStage' | 'interessadoLigarEmMeses' | 'dataPerda' | 'selecionado_backlog_perdidos' | 'observacao'>>
  ): Promise<LostLeadCard> {
    const response = await this.api.put<LostLeadCard>(`/lost-cards/${lostCardId}`, updates);
    return response.data;
  }

  async updateLostLeadBacklogSelection(lostCardIds: string[], selected = true): Promise<void> {
    await this.api.put('/lost-cards/backlog', {
      lostCardIds,
      selected,
    });
  }

  // Mensagens
  async getMensagens(leadId: string): Promise<Mensagem[]> {
    const response = await this.api.get<ListResponse<Mensagem>>(`/leads/${leadId}/mensagens`);
    return (response.data.mensagens as Mensagem[]) || [];
  }

  async sendMensagem(
    leadId: string,
    payload: { conteudo?: string; tipo?: 'text' | 'image' | 'document' | 'audio'; mediaUrl?: string; s3Key?: string; mimeType?: string }
  ): Promise<Mensagem> {
    const response = await this.api.post<Mensagem>(`/leads/${leadId}/mensagens`, payload);
    return response.data;
  }

  async syncLeadMessages(leadId: string): Promise<{ message: string; newMessages: number }> {
    const response = await this.api.post<{ message: string; newMessages: number }>(`/leads/${leadId}/mensagens/sync`);
    return response.data;
  }

  async markMessagesAsRead(leadId: string): Promise<void> {
    await this.api.post(`/leads/${leadId}/mensagens/read`);
  }

  /**
   * Fetch media (audio/image) via proxy - uses auth, returns blob URL for <audio>/<img> src.
   * Call URL.revokeObjectURL when done to avoid memory leaks.
   */
  async getMediaBlobUrl(leadId: string, messageId: string): Promise<string> {
    const response = await this.api.get<Blob>(`/leads/${leadId}/mensagens/${messageId}/media`, {
      responseType: 'blob',
    });
    const contentType = (response.headers['content-type'] || 'audio/mp4').split(';')[0].trim();
    const blob = new Blob([response.data], { type: contentType });
    return URL.createObjectURL(blob);
  }

  // Anexos
  async getUploadUrl(leadId: string, fileName: string, mimeType?: string): Promise<{
    uploadUrl: string;
    s3Key: string;
    anexoId: string;
    expiresIn: number;
    fileName: string;
    mimeType: string;
  }> {
    const response = await this.api.post(`/leads/${leadId}/anexos/upload-url`, { fileName, mimeType });
    return response.data;
  }

  async confirmUpload(leadId: string, anexoId: string, data: {
    s3Key: string;
    fileName: string;
    mimeType: string;
    fileSize?: number;
  }): Promise<{ anexo: Anexo; totalAnexos: number }> {
    const response = await this.api.post(`/leads/${leadId}/anexos/${anexoId}/confirm`, data);
    return response.data;
  }

  async getAnexos(leadId: string): Promise<{ anexos: Anexo[]; total: number }> {
    const response = await this.api.get(`/leads/${leadId}/anexos`);
    return response.data;
  }

  async getDownloadUrl(leadId: string, anexoId: string): Promise<{
    downloadUrl: string;
    expiresIn: number;
    fileName: string;
    mimeType: string;
  }> {
    const response = await this.api.get(`/leads/${leadId}/anexos/${anexoId}/download-url`);
    return response.data;
  }

  async deleteAnexo(leadId: string, anexoId: string): Promise<{ message: string; remainingAnexos: number }> {
    const response = await this.api.delete(`/leads/${leadId}/anexos/${anexoId}`);
    return response.data;
  }

  // Clientes
  async getClientes(telefone?: string): Promise<Cliente[]> {
    const params = telefone ? { telefone } : {};
    const response = await this.api.get<ListResponse<Cliente>>('/clientes', { params });
    return (response.data.clientes as Cliente[]) || [];
  }

  async getCliente(clienteId: string): Promise<Cliente> {
    const response = await this.api.get<Cliente>(`/clientes/${clienteId}`);
    return response.data;
  }

  async createCliente(cliente: Partial<Cliente>): Promise<Cliente> {
    const response = await this.api.post<Cliente>('/clientes', cliente);
    return response.data;
  }

  async updateCliente(clienteId: string, cliente: Partial<Cliente>): Promise<Cliente> {
    const response = await this.api.put<Cliente>(`/clientes/${clienteId}`, cliente);
    return response.data;
  }

  async deleteCliente(clienteId: string): Promise<void> {
    await this.api.delete(`/clientes/${clienteId}`);
  }

  async associateLeadToCliente(clienteId: string, leadId: string): Promise<void> {
    await this.api.post(`/clientes/${clienteId}/leads/${leadId}`);
  }

  // Usuários (Admin only)
  async getUsuarios(): Promise<Usuario[]> {
    const response = await this.api.get<ListResponse<Usuario>>('/usuarios');
    return (response.data.usuarios as Usuario[]) || [];
  }

  async getAtendentes(): Promise<Usuario[]> {
    const response = await this.api.get<{ atendentes: Usuario[] }>('/usuarios/atendentes');
    return response.data.atendentes || [];
  }

  // Campanhas (Kanban de Campanhas)
  async listCampaigns(params?: {
    status?: Campaign['status'];
    attendanceMode?: 'human_kanban' | 'ai_funnel';
    period?: 'today' | 'week' | 'month' | 'year' | 'all';
  }): Promise<Campaign[]> {
    const response = await this.api.get<{ campaigns: Campaign[] }>('/campaigns', {
      params: params
        ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null))
        : undefined,
    });
    return response.data.campaigns || [];
  }

  async getCampaign(campaignId: string): Promise<Campaign> {
    const response = await this.api.get<{ campaign: Campaign }>(`/campaigns/${campaignId}`);
    return response.data.campaign;
  }

  async listCampaignItems(params?: {
    stage?: CampaignItemPipelineStage;
    campaignId?: string;
    campaignIds?: string[];
  }): Promise<CampaignItem[]> {
    const response = await this.api.get<{ items: CampaignItem[] }>('/campaign-items', {
      params: {
        stage: params?.stage,
        campaignId: params?.campaignId,
        campaignIds: params?.campaignIds?.length ? params.campaignIds.join(',') : undefined,
      },
    });
    return response.data.items || [];
  }

  async createCampaign(body: CreateCampaignRequest): Promise<Campaign> {
    const response = await this.api.post<{ campaign: Campaign }>('/campaigns', body);
    return response.data.campaign;
  }

  async patchCampaign(campaignId: string, body: UpdateCampaignRequest): Promise<Campaign> {
    const response = await this.api.patch<{ campaign: Campaign }>(`/campaigns/${campaignId}`, body);
    return response.data.campaign;
  }

  async finalizeCampaign(campaignId: string): Promise<{
    campaign: Campaign;
    movedItems: Array<
      Pick<CampaignItem, 'campaignItemId' | 'campaignId' | 'pipelineStage' | 'updatedAt' | 'interessadoLigarEmMeses'>
    >;
  }> {
    const response = await this.api.post<{
      campaign: Campaign;
      movedItems: Array<
        Pick<CampaignItem, 'campaignItemId' | 'campaignId' | 'pipelineStage' | 'updatedAt' | 'interessadoLigarEmMeses'>
      >;
    }>(`/campaigns/${campaignId}/finalize`, {});
    return {
      campaign: response.data.campaign,
      movedItems: response.data.movedItems || [],
    };
  }

  async addLostCardsToCampaign(
    campaignId: string,
    lostCardIds: string[]
  ): Promise<AddCampaignItemsApiResult> {
    const response = await this.api.post<{
      items: CampaignItem[];
      total: number;
      skippedUnsubscribed?: AddCampaignItemsApiResult['skippedUnsubscribed'];
    }>(`/campaigns/${campaignId}/items`, { lostCardIds });
    return {
      items: response.data.items || [],
      skippedUnsubscribed: response.data.skippedUnsubscribed || [],
    };
  }

  async updateCampaignItem(
    campaignItemId: string,
    body: Partial<{
      pipelineStage: CampaignItemPipelineStage;
      disparoStatus: CampaignItem['disparoStatus'];
      disparadoAt: string;
      repliedAt: string;
      interessadoLigarEmMeses: number;
    }>
  ): Promise<CampaignItem> {
    const response = await this.api.put<{ item: CampaignItem }>(`/campaign-items/${campaignItemId}`, body);
    return response.data.item;
  }

  async createUsuario(usuario: Partial<Usuario>): Promise<Usuario> {
    const response = await this.api.post<Usuario>('/usuarios', usuario);
    return response.data;
  }

  async updateUsuario(usuarioId: string, usuario: Partial<Usuario>): Promise<Usuario> {
    const response = await this.api.put<Usuario>(`/usuarios/${usuarioId}`, usuario);
    return response.data;
  }

  async updateUsuarioWhatsApp(usuarioId: string, whatsappAccountId: string): Promise<Usuario> {
    const response = await this.api.put<Usuario>(`/usuarios/${usuarioId}/whatsapp-account`, { whatsappAccountId });
    return response.data;
  }

  // WhatsApp Accounts (Admin only)
  async getWhatsAppAccounts(): Promise<WhatsAppAccount[]> {
    const response = await this.api.get<ListResponse<WhatsAppAccount>>('/whatsapp-accounts');
    return (response.data.accounts as WhatsAppAccount[]) || [];
  }

  async createWhatsAppAccount(account: Partial<WhatsAppAccount>): Promise<WhatsAppAccount> {
    const response = await this.api.post<WhatsAppAccount>('/whatsapp-accounts', account);
    return response.data;
  }

  async updateWhatsAppAccount(accountId: string, account: Partial<WhatsAppAccount>): Promise<WhatsAppAccount> {
    const response = await this.api.put<WhatsAppAccount>(`/whatsapp-accounts/${accountId}`, account);
    return response.data;
  }

  async connectWhatsAppAccount(accountId: string): Promise<{ qrCode: string; status: string; message: string }> {
    const response = await this.api.post(`/whatsapp-accounts/${accountId}/connect`);
    return response.data;
  }

  async getWhatsAppAccountStatus(accountId: string): Promise<{ status: string; ultimaConexaoAt?: string; qrCode?: string; pairingCode?: string }> {
    const response = await this.api.get(`/whatsapp-accounts/${accountId}/status`);
    return response.data;
  }

  async disconnectWhatsAppAccount(accountId: string): Promise<WhatsAppAccount> {
    const response = await this.api.post<WhatsAppAccount>(`/whatsapp-accounts/${accountId}/disconnect`);
    return response.data;
  }

  // Plans (Public)
  async getPlans(): Promise<Plan[]> {
    const response = await this.api.get<PlansResponse>('/plans');
    return response.data.plans || [];
  }

  // Auth - Register
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await this.api.post<RegisterResponse>('/auth/register', data);
    return response.data;
  }

  // Organizations
  async getOrganization(orgId: string): Promise<Organization> {
    const response = await this.api.get<Organization>(`/organizations/${orgId}`);
    return response.data;
  }

  async updateOrganization(orgId: string, data: UpdateOrganizationRequest): Promise<Organization> {
    const response = await this.api.put<Organization>(`/organizations/${orgId}`, data);
    return response.data;
  }

  async getOrganizationUsers(orgId: string): Promise<OrganizationUsersResponse> {
    const response = await this.api.get<OrganizationUsersResponse>(`/organizations/${orgId}/users`);
    return response.data;
  }

  async inviteUser(orgId: string, data: InviteUserRequest): Promise<InviteUserResponse> {
    const response = await this.api.post<InviteUserResponse>(`/organizations/${orgId}/users`, data);
    return response.data;
  }

  async removeUserFromOrganization(orgId: string, userId: string): Promise<void> {
    await this.api.delete(`/organizations/${orgId}/users/${userId}`);
  }

  async resendInvite(orgId: string, inviteId: string): Promise<{ message: string; invite: any }> {
    const response = await this.api.post<{ message: string; invite: any }>(`/organizations/${orgId}/invites/${inviteId}/resend`);
    return response.data;
  }

  async updateUserRole(orgId: string, userId: string, role: 'admin' | 'gerente' | 'atendente'): Promise<OrganizationUser> {
    const response = await this.api.put<OrganizationUser>(`/organizations/${orgId}/users/${userId}`, { role });
    return response.data;
  }

  async changePlan(orgId: string, data: ChangePlanRequest): Promise<Organization> {
    const response = await this.api.put<Organization>(`/organizations/${orgId}/plan`, data);
    return response.data;
  }

  async getOrganizationUsage(orgId: string): Promise<OrganizationUsageStats> {
    const response = await this.api.get<OrganizationUsageStats>(`/organizations/${orgId}/usage`);
    return response.data;
  }

  async getDashboardStats(
    orgId: string,
    params?: { year: number; month: string }
  ): Promise<DashboardStatsResponse> {
    const response = await this.api.get<DashboardStatsResponse>(
      `/organizations/${orgId}/dashboard-stats`,
      params ? { params: { year: params.year, month: params.month } } : undefined
    );
    return response.data;
  }

  async getDashboardGoals(orgId: string): Promise<DashboardGoals> {
    const response = await this.api.get<{ goals: DashboardGoals }>(`/organizations/${orgId}/dashboard-goals`);
    return response.data.goals;
  }

  async updateDashboardGoals(orgId: string, goals: Partial<DashboardGoals>): Promise<DashboardGoals> {
    const response = await this.api.put<{ goals: DashboardGoals }>(`/organizations/${orgId}/dashboard-goals`, goals);
    return response.data.goals;
  }

  async updateCampaignRemarketingDefaults(
    orgId: string,
    defaultRemarketingMeses: number | null
  ): Promise<{ defaultRemarketingMeses: number | null }> {
    const response = await this.api.put<{ defaultRemarketingMeses: number | null }>(
      `/organizations/${orgId}/campaign-remarketing-defaults`,
      { defaultRemarketingMeses }
    );
    return response.data;
  }

  async listUnsubscribedPhones(params?: {
    period?: string;
    source?: string;
    includeRevoked?: boolean;
    campaignId?: string;
    q?: string;
    limit?: number;
    nextToken?: string;
  }): Promise<ListUnsubscribedPhonesResponse> {
    const response = await this.api.get<ListUnsubscribedPhonesResponse>('/unsubscribed-phones', {
      params: {
        period: params?.period,
        source: params?.source,
        includeRevoked: params?.includeRevoked,
        campaignId: params?.campaignId,
        q: params?.q,
        limit: params?.limit,
        nextToken: params?.nextToken,
      },
    });
    return response.data;
  }

  async revokeUnsubscribedPhone(
    unsubscribeId: string,
    reason: string
  ): Promise<{ ok?: boolean; message?: string; alreadyRevoked?: boolean }> {
    const response = await this.api.post<{ ok?: boolean; message?: string; alreadyRevoked?: boolean }>(
      `/unsubscribed-phones/${encodeURIComponent(unsubscribeId)}/revoke`,
      { reason }
    );
    return response.data;
  }

  async bulkDeleteUnsubscribedPhones(unsubscribeIds: string[]): Promise<BulkDeleteUnsubscribedResponse> {
    const response = await this.api.post<BulkDeleteUnsubscribedResponse>('/unsubscribed-phones/bulk-delete', {
      unsubscribeIds,
    });
    return response.data;
  }

  // Invites
  async getInvite(token: string): Promise<{ invite: InviteUserResponse['invite']; organization: Organization }> {
    const response = await this.api.get(`/invites/${token}`);
    return response.data;
  }

  async acceptInvite(token: string, senha: string, nome?: string): Promise<{ user: Usuario; token: string; organization: Organization }> {
    const response = await this.api.post(`/invites/${token}/accept`, { senha, nome });
    return response.data;
  }

  // Billing / Subscriptions
  async createCheckoutSession(planId: string, billingCycle: BillingCycle): Promise<CheckoutSessionResponse> {
    const response = await this.api.post<CheckoutSessionResponse>('/billing/checkout-session', {
      planId,
      billingCycle,
    });
    return response.data;
  }

  async createCustomerPortalSession(): Promise<CustomerPortalResponse> {
    const response = await this.api.post<CustomerPortalResponse>('/billing/customer-portal');
    return response.data;
  }

  async getSubscription(): Promise<Subscription | null> {
    const response = await this.api.get<SubscriptionResponse>('/billing/subscription');
    return response.data.subscription;
  }

  async cancelSubscription(): Promise<Subscription> {
    const response = await this.api.post<{ subscription: Subscription }>('/billing/subscription/cancel');
    return response.data.subscription;
  }

  // -------------------------------------------------------------------------
  // AI Service - Suggestions
  // -------------------------------------------------------------------------

  async getAISuggestion(
    leadId: string,
    mensagens: Pick<Mensagem, 'messageId' | 'conteudo' | 'direcao' | 'timestamp' | 'enviadoPor'>[],
    inputValue?: string,
    roleId?: string
  ): Promise<{ suggestions: AISuggestion[]; reasoning: string }> {
    // Uses aiApi which routes directly to AI Service Lambda
    const response = await this.aiApi.post<{ suggestions: AISuggestion[]; reasoning: string }>('/ai/suggest', {
      leadId,
      mensagens,
      inputValue,
      roleId,
    });
    return response.data;
  }

  // -------------------------------------------------------------------------
  // AI Service - Guidelines
  // -------------------------------------------------------------------------

  async getGuidelines(tipo?: GuidelineTipo): Promise<Guideline[]> {
    const params = tipo ? { tipo } : {};
    const response = await this.api.get<{ guidelines: Guideline[] }>('/guidelines', { params });
    return response.data.guidelines;
  }

  async getGuideline(guidelineId: string): Promise<Guideline> {
    const response = await this.api.get<Guideline>(`/guidelines/${guidelineId}`);
    return response.data;
  }

  async createGuideline(data: CreateGuidelineRequest): Promise<Guideline> {
    const response = await this.api.post<Guideline>('/guidelines', data);
    return response.data;
  }

  async updateGuideline(guidelineId: string, data: Partial<CreateGuidelineRequest>): Promise<Guideline> {
    const response = await this.api.put<Guideline>(`/guidelines/${guidelineId}`, data);
    return response.data;
  }

  async deleteGuideline(guidelineId: string): Promise<void> {
    await this.api.delete(`/guidelines/${guidelineId}`);
  }

  async getPriceSources(): Promise<PriceSource[]> {
    const response = await this.api.get<{ priceSources: PriceSource[] }>('/price-sources');
    return response.data.priceSources || [];
  }

  async createPriceSource(data: CreatePriceSourceRequest): Promise<PriceSource> {
    const response = await this.api.post<{ priceSource: PriceSource }>('/price-sources', data);
    return response.data.priceSource;
  }

  async deletePriceSource(priceSourceId: string): Promise<void> {
    await this.api.delete(`/price-sources/${priceSourceId}`);
  }

  async getOrchestratorPromptPack(): Promise<OrchestratorPromptPack> {
    const response = await this.api.get<{ pack: OrchestratorPromptPack }>('/orchestrator-prompts');
    return response.data.pack;
  }

  async saveOrchestratorPromptPack(agents: OrchestratorAgentPrompt[]): Promise<OrchestratorPromptPack> {
    const response = await this.api.put<{ pack: OrchestratorPromptPack }>('/orchestrator-prompts', { agents });
    return response.data.pack;
  }

  async getOrchestratorPromptCatalog(): Promise<OrchestratorPromptCatalogResponse> {
    const response = await this.api.get<OrchestratorPromptCatalogResponse>('/orchestrator-prompts/catalog');
    const data = response.data;
    return {
      prompts: data.prompts || [],
      adminHints: {
        profileDomainExample: data.adminHints?.profileDomainExample || '',
      },
    };
  }

  async saveOrchestratorPromptOverride(
    promptId: string,
    payload: {
      overridePrompt?: string;
      examplesPositive?: string[];
      examplesNegative?: string[];
      threshold?: number;
    }
  ): Promise<PromptCatalogItem[]> {
    const response = await this.api.put<{ prompts: PromptCatalogItem[] }>(
      `/orchestrator-prompts/${encodeURIComponent(promptId)}/override`,
      payload
    );
    return response.data.prompts || [];
  }

  async restoreOrchestratorPromptDefault(promptId: string): Promise<PromptCatalogItem[]> {
    const response = await this.api.post<{ prompts: PromptCatalogItem[] }>(
      `/orchestrator-prompts/${encodeURIComponent(promptId)}/restore-default`,
      {}
    );
    return response.data.prompts || [];
  }

  async listAdminSpecialistPrompts(filters?: {
    scope?: SpecialistPromptScope;
    organizationId?: string;
  }): Promise<SpecialistPrompt[]> {
    const response = await this.api.get<{ specialists: SpecialistPrompt[] }>('/admin/specialist-prompts', {
      params: {
        ...(filters?.scope ? { scope: filters.scope } : {}),
        ...(filters?.organizationId ? { organizationId: filters.organizationId } : {}),
      },
    });
    return response.data.specialists || [];
  }

  async getAdminSpecialistPrompt(specialistId: string): Promise<SpecialistPrompt> {
    const response = await this.api.get<{ specialist: SpecialistPrompt }>(
      `/admin/specialist-prompts/${encodeURIComponent(specialistId)}`
    );
    return response.data.specialist;
  }

  async createAdminSpecialistPrompt(payload: {
    name: string;
    description: string;
    promptText: string;
    returnContract: SpecialistReturnContract;
    scope: SpecialistPromptScope;
    organizationId?: string;
    isActive?: boolean;
  }): Promise<SpecialistPrompt> {
    const response = await this.api.post<{ specialist: SpecialistPrompt }>('/admin/specialist-prompts', payload);
    return response.data.specialist;
  }

  async updateAdminSpecialistPrompt(
    specialistId: string,
    payload: Partial<{
      name: string;
      description: string;
      promptText: string;
      returnContract: SpecialistReturnContract;
      scope: SpecialistPromptScope;
      organizationId: string | null;
      isActive: boolean;
    }>
  ): Promise<SpecialistPrompt> {
    const response = await this.api.put<{ specialist: SpecialistPrompt }>(
      `/admin/specialist-prompts/${encodeURIComponent(specialistId)}`,
      payload
    );
    return response.data.specialist;
  }

  async deactivateAdminSpecialistPrompt(specialistId: string): Promise<SpecialistPrompt> {
    const response = await this.api.delete<{ specialist: SpecialistPrompt }>(
      `/admin/specialist-prompts/${encodeURIComponent(specialistId)}`
    );
    return response.data.specialist;
  }

  async getActiveSpecialistPrompts(organizationId?: string): Promise<SpecialistPrompt[]> {
    const response = await this.api.get<{ specialists: SpecialistPrompt[] }>('/specialist-prompts/active', {
      params: organizationId ? { organizationId } : {},
    });
    return response.data.specialists || [];
  }

  async createSpecialistPromptOverride(payload: {
    baseSpecialistId: string;
    promptText: string;
    name?: string;
    description?: string;
  }): Promise<SpecialistPrompt> {
    const response = await this.api.post<{ specialist: SpecialistPrompt }>('/specialist-prompts/overrides', payload);
    return response.data.specialist;
  }

  async startLeadIa(leadId: string): Promise<Lead> {
    // Backend responde rápido (orquestração continua em segundo Lambda); 45s cobre rede lenta
    const response = await this.api.post<Lead>(`/leads/${leadId}/ia/start`, {}, { timeout: 45_000 });
    return response.data;
  }

  async assumeHumanLeadIa(leadId: string): Promise<Lead> {
    const response = await this.api.post<Lead>(`/leads/${leadId}/ia/assume-human`, {});
    return response.data;
  }

  async stopLeadIa(leadId: string): Promise<Lead> {
    const response = await this.api.post<Lead>(`/leads/${leadId}/ia/stop`, {});
    return response.data;
  }

  async sendLeadPriceDraft(leadId: string): Promise<Lead> {
    const response = await this.api.post<Lead>(`/leads/${leadId}/ia/send-price-draft`, {});
    return response.data;
  }

  async indexGuideline(guidelineId: string): Promise<void> {
    // This calls the AI service to index the guideline in Pinecone
    // Uses aiApi which routes directly to /ai/* endpoints
    await this.aiApi.post(`/ai/guidelines/index`, { guidelineId });
  }

  async getRagWebPages(): Promise<RagWebPage[]> {
    const response = await this.api.get<{ ragWebPages: RagWebPage[] }>('/rag-web-pages');
    return response.data.ragWebPages || [];
  }

  async createRagWebPage(data: CreateRagWebPageRequest): Promise<RagWebPage> {
    const response = await this.api.post<{ ragWebPage: RagWebPage }>('/rag-web-pages', data);
    return response.data.ragWebPage;
  }

  async updateRagWebPage(pageId: string, data: UpdateRagWebPageRequest): Promise<RagWebPage> {
    const response = await this.api.put<{ ragWebPage: RagWebPage }>(`/rag-web-pages/${pageId}`, data);
    return response.data.ragWebPage;
  }

  /** Remove vectors from Pinecone, then caller should delete the row via deleteRagWebPage. */
  async deleteRagWebPageVectors(pageId: string): Promise<void> {
    await this.aiApi.delete(`/ai/web-pages/${pageId}`);
  }

  async deleteRagWebPage(pageId: string): Promise<void> {
    await this.api.delete(`/rag-web-pages/${pageId}`);
  }

  async indexRagWebPage(pageId: string): Promise<void> {
    await this.aiApi.post(`/ai/web-pages/index`, { pageId });
  }

  /**
   * Generate presigned URL for guideline file upload
   */
  async getGuidelineUploadUrl(fileName: string, contentType: string, fileSize: number): Promise<{
    uploadUrl: string;
    s3Key: string;
    guidelineId: string;
    expiresIn: number;
  }> {
    const response = await this.api.post<{
      uploadUrl: string;
      s3Key: string;
      guidelineId: string;
      expiresIn: number;
    }>('/guidelines/upload-url', { fileName, contentType, fileSize });
    return response.data;
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('S3 upload failed:', response.status, errorText);
      throw new Error(`Failed to upload file to S3: ${response.status}`);
    }
  }

  /**
   * Create guideline with file upload to S3
   */
  async createGuidelineWithFile(
    data: Omit<CreateGuidelineRequest, 's3Key' | 'sourceFile'>,
    file: File
  ): Promise<Guideline> {
    // 1. Get presigned upload URL
    const { uploadUrl, s3Key } = await this.getGuidelineUploadUrl(
      file.name,
      file.type,
      file.size
    );

    // 2. Upload file to S3
    await this.uploadFileToS3(uploadUrl, file);

    // 3. Create guideline with S3 reference
    return this.createGuideline({
      ...data,
      s3Key,
      sourceFile: file.name,
      conteudo: '', // Content will be extracted by AI service during indexing
    });
  }

  // -------------------------------------------------------------------------
  // AI Service - Roles
  // -------------------------------------------------------------------------

  async getAIRoles(): Promise<AIRole[]> {
    const response = await this.api.get<{ roles: AIRole[] }>('/ai-roles');
    return response.data.roles;
  }

  async getAIRole(roleId: string): Promise<AIRole> {
    const response = await this.api.get<AIRole>(`/ai-roles/${roleId}`);
    return response.data;
  }

  async createAIRole(data: CreateAIRoleRequest): Promise<AIRole> {
    const response = await this.api.post<AIRole>('/ai-roles', data);
    return response.data;
  }

  async updateAIRole(roleId: string, data: Partial<CreateAIRoleRequest>): Promise<AIRole> {
    const response = await this.api.put<AIRole>(`/ai-roles/${roleId}`, data);
    return response.data;
  }

  async deleteAIRole(roleId: string): Promise<void> {
    await this.api.delete(`/ai-roles/${roleId}`);
  }

  async getAIRoleTemplates(): Promise<AIRoleTemplate[]> {
    // Uses aiApi which routes directly to AI Service Lambda
    const response = await this.aiApi.get<{ templates: AIRoleTemplate[] }>('/ai/roles/templates');
    return response.data.templates;
  }

  // -------------------------------------------------------------------------
  // AI Service - Settings
  // -------------------------------------------------------------------------

  async getAISettings(orgId: string): Promise<AISettings> {
    const response = await this.api.get<{ aiSettings: AISettings }>(`/organizations/${orgId}/ai-settings`);
    return response.data.aiSettings;
  }

  async updateAISettings(orgId: string, settings: Partial<AISettings>): Promise<AISettings> {
    const response = await this.api.put<{ aiSettings: AISettings }>(`/organizations/${orgId}/ai-settings`, settings);
    return response.data.aiSettings;
  }

  // -------------------------------------------------------------------------
  // Kanban - Pós-Vendas settings
  // -------------------------------------------------------------------------

  async getPosVendasSettings(orgId: string): Promise<PosVendasSettings> {
    const response = await this.api.get<{ posVendasSettings: PosVendasSettings }>(
      `/organizations/${orgId}/pos-vendas-settings`
    );
    return response.data.posVendasSettings;
  }

  async updatePosVendasSettings(
    orgId: string,
    settings: PosVendasSettings
  ): Promise<PosVendasSettings> {
    const response = await this.api.put<{ posVendasSettings: PosVendasSettings }>(
      `/organizations/${orgId}/pos-vendas-settings`,
      settings
    );
    return response.data.posVendasSettings;
  }

  // -------------------------------------------------------------------------
  // Kanban - Column labels
  // -------------------------------------------------------------------------

  async getKanbanColumnLabels(orgId: string): Promise<KanbanColumnLabels> {
    const response = await this.api.get<{ kanbanColumnLabels: KanbanColumnLabels }>(
      `/organizations/${orgId}/kanban-column-labels`
    );
    return response.data.kanbanColumnLabels;
  }

  async updateKanbanColumnLabels(
    orgId: string,
    labels: KanbanColumnLabels
  ): Promise<KanbanColumnLabels> {
    const response = await this.api.put<{ kanbanColumnLabels: KanbanColumnLabels }>(
      `/organizations/${orgId}/kanban-column-labels`,
      labels
    );
    return response.data.kanbanColumnLabels;
  }

  // -------------------------------------------------------------------------
  // Admin - Trial Management (Super Admin only)
  // -------------------------------------------------------------------------

  async searchOrganizations(search?: string): Promise<{ organizations: any[]; total: number }> {
    const params = search ? { search } : {};
    const response = await this.api.get<{ organizations: any[]; total: number }>('/admin/organizations', { params });
    return response.data;
  }

  async getActiveTrials(): Promise<{ organizations: any[]; total: number }> {
    const response = await this.api.get<{ organizations: any[]; total: number }>('/admin/trials');
    return response.data;
  }

  async activateTrial(orgId: string, limits: { maxUsers: number; maxLeadsPerMonth: number; maxWhatsAppAccounts: number }): Promise<{ message: string; trial: TrialInfo }> {
    const response = await this.api.post<{ message: string; trial: TrialInfo }>(`/admin/organizations/${orgId}/trial`, limits);
    return response.data;
  }

  async renewTrial(orgId: string, limits?: { maxUsers?: number; maxLeadsPerMonth?: number; maxWhatsAppAccounts?: number }): Promise<{ message: string; trial: TrialInfo }> {
    const response = await this.api.put<{ message: string; trial: TrialInfo }>(`/admin/organizations/${orgId}/trial/renew`, limits || {});
    return response.data;
  }

  async deactivateTrial(orgId: string): Promise<{ message: string }> {
    const response = await this.api.put<{ message: string }>(`/admin/organizations/${orgId}/trial/deactivate`);
    return response.data;
  }

  // Notifications
  async listNotifications(): Promise<AppNotification[]> {
    const response = await this.api.get<AppNotification[]>('/notifications');
    return response.data;
  }

  async markNotificationAsRead(notificationId: string): Promise<AppNotification> {
    const response = await this.api.put<AppNotification>(`/notifications/${notificationId}/read`);
    return response.data;
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await this.api.delete(`/notifications/${notificationId}`);
  }

  // Imported Leads (CSV)

  async createImportedLeads(leads: { nome: string; telefone: string; cpf?: string; email?: string }[]): Promise<ImportedLead[]> {
    const response = await this.api.post<{ importedLeads: ImportedLead[]; total: number }>('/imported-leads', { leads });
    return response.data.importedLeads || [];
  }

  async getImportedLeads(): Promise<ImportedLead[]> {
    const response = await this.api.get<{ importedLeads: ImportedLead[]; total: number }>('/imported-leads');
    return response.data.importedLeads || [];
  }

  async deleteImportedLead(importedLeadId: string): Promise<void> {
    await this.api.delete(`/imported-leads/${importedLeadId}`);
  }

  // Campaign multi-source

  async addPartnersToCampaign(
    campaignId: string,
    clienteIds: string[]
  ): Promise<AddCampaignItemsApiResult> {
    const response = await this.api.post<{
      items: CampaignItem[];
      total: number;
      skippedUnsubscribed?: AddCampaignItemsApiResult['skippedUnsubscribed'];
    }>(`/campaigns/${campaignId}/partners`, { clienteIds });
    return {
      items: response.data.items || [],
      skippedUnsubscribed: response.data.skippedUnsubscribed || [],
    };
  }

  async addImportedLeadsToCampaign(
    campaignId: string,
    importedLeadIds: string[]
  ): Promise<AddCampaignItemsApiResult> {
    const response = await this.api.post<{
      items: CampaignItem[];
      total: number;
      skippedUnsubscribed?: AddCampaignItemsApiResult['skippedUnsubscribed'];
    }>(`/campaigns/${campaignId}/imported-leads`, { importedLeadIds });
    return {
      items: response.data.items || [],
      skippedUnsubscribed: response.data.skippedUnsubscribed || [],
    };
  }

  async addCsvLeadsToCampaign(
    campaignId: string,
    leads: CsvTransientLeadInput[],
    batchId?: string
  ): Promise<AddCsvCampaignItemsApiResult> {
    const response = await this.api.post<AddCsvCampaignItemsApiResult>(
      `/campaigns/${campaignId}/csv-items`,
      { leads, ...(batchId ? { batchId } : {}) }
    );
    return response.data;
  }

  // Disparos
  async createDisparo(body: CreateDisparoRequest): Promise<Disparo> {
    const response = await this.api.post<{ disparo: Disparo }>('/disparos', body);
    return response.data.disparo;
  }

  async getDisparos(): Promise<Disparo[]> {
    const response = await this.api.get<{ disparos: Disparo[] }>('/disparos');
    return response.data.disparos || [];
  }

  async getDisparoById(id: string): Promise<Disparo> {
    const response = await this.api.get<{ disparo: Disparo }>(`/disparos/${id}`);
    return response.data.disparo;
  }

  async updateDisparoStatus(id: string, status: DisparoStatus): Promise<Disparo> {
    const response = await this.api.put<{ disparo: Disparo }>(`/disparos/${id}/status`, { status });
    return response.data.disparo;
  }

  async patchDisparoConfig(id: string, body: PatchDisparoConfigRequest): Promise<Disparo> {
    const response = await this.api.patch<{ disparo: Disparo }>(`/disparos/${id}`, body);
    return response.data.disparo;
  }
}

export const apiService = new ApiService();
